<template>
  <div class="form-field form-field-checkbox">
    <label :for="field.name" class="container">
      <input :id="field.name" v-model="fieldValue" type="checkbox" />
      <div class="checkmark">
        <icon class="icon" name="checkbox"></icon>
      </div>
      <span class="label">
        <component
          :is="label.component"
          v-for="(label, index) in labels"
          :key="index"
          to="/cgu"
          target="_blank"
          >{{ label.label }}</component
        >
      </span>
    </label>
  </div>
</template>
<script setup lang="ts">
interface Props {
  field: FormField;
}

const { isMounted } = useIsMounted();

const props = defineProps({
  field: null
});

const {
  formFieldsData: { memberValues },
  checkValidatedFormOnMounted,
} = inject(INJECT_FORM_PAGE);

const blured = ref<boolean>(checkValidatedFormOnMounted.value);

const fieldValue = computed({
  get() {
    return memberValues.value[props.field.name].value;
  },
  set(value) {
    memberValues.value[props.field.name].value = value;
  },
});
const labelLinks = computed(() => props.field.labelLinks);
const extractLinksAndContext = (text) => {
  const regex = /\[\[([^|\]]+)\|([^|\]]+)\]\]/g;

  const matches = [...text.matchAll(regex)];
  const textes = [];

  if (matches.length === 0) return [{ label: text, component: 'span' }];
  matches.forEach((match, index) => {
    const [fullMatch, link, label] = match;
    textes.push({ label: text.substring(0, match.index), component: 'span' });
    textes.push({
      label,
      link: labelLinks.value[index],
      component: resolveComponent('NuxtLink'),
    });
    textes.push({
      label: text.substring(match.index + fullMatch.length),
      component: 'span',
    });
  });

  return textes;
};

const labels = computed(() => {
  const labels = extractLinksAndContext(props.field.label);

  return isFieldRequired(props.field)
    ? [...labels, { label: '*', component: 'span' }]
    : labels;
});

const { messages } = useFormFieldValidation(props.field, memberValues);

const feedback = computed(() => {
  let feedbackMessages = [''];
  if (blured.value && messages.value.length > 0 && isMounted.value) {
    feedbackMessages = [...messages.value];
  }
  return feedbackMessages;
});
</script>
<style lang="scss" scoped>
.form-field-checkbox {
  position: relative;
  z-index: 0;
}

.checkmark {
  width: 3rem;
  height: 3rem;
  transition: border 300ms $ease-out-quad;
  border: solid var(--stroke_thickness_l) var(--color_border);
  border-radius: var(--stroke_radius_n);
  pointer-events: none;
  &::after {
    content: '';
    position: absolute;
  }
  .icon {
    transform: scale(0);
    transition: opacity 300ms $ease-out-quad, transform 200ms $ease-in-back;
    opacity: 0;
    color: var(--color_primary);
  }
}

label input {
  position: absolute;
  opacity: 0;
  &:hover,
  &:focus {
    ~ .checkmark {
      border: solid var(--stroke_thickness_l) var(--color_primary);
    }
  }
  &:checked {
    ~ .checkmark {
      .icon {
        transform: scale(1);
        transition: opacity 300ms $ease-out-quad, transform 200ms $ease-out-back;
        opacity: 1;
      }
    }
  }
}

label {
  display: grid;
  gap: var(--gap_n);
  position: relative;
  grid-template-columns: auto 1fr;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  a {
    display: inline;
    position: relative;
    z-index: 1;
    color: var(--color_primary);

    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
  &:focus {
    color: var(--color_secondary);
  }
}
</style>
